import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material'
import { t } from 'i18n'
import React, { useRef } from 'react'

interface ReplaceConfirmationDialogProps {
  open: boolean
  loading?: boolean
  onClose: () => void
  onConfirm: (file: File) => void
}

const ReplaceConfirmationDialog: React.FC<ReplaceConfirmationDialogProps> = ({
  open,
  loading = false,
  onClose,
  onConfirm
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleConfirm = () => {
    // Only trigger file picker if not loading
    if (!loading) {
      fileInputRef.current?.click()
    }
  }

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      onConfirm(file)
      // Don't close the dialog here - let the upload process handle it
    }
    // Reset the input value so the same file can be selected again if needed
    event.target.value = ''
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('heatmaps.areYouSure') || 'Are you sure?'}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('heatmaps.replaceWarning') ||
            'Replacing this heatmap will affect how existing injury data appears on the map. Previous injury locations may not align correctly with the new heatmap.'}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" disabled={loading}>
          {t('cancel') || 'Cancel'}
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {loading
            ? t('heatmaps.uploading') || 'Uploading...'
            : t('heatmaps.replace') || 'Replace'}
        </Button>
      </DialogActions>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileSelected}
      />
    </Dialog>
  )
}

export default ReplaceConfirmationDialog
